import React, { useEffect, useState } from "react";
import Modal from "../../../shared/ui/Modal";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import CaseIdBadge from "../../../shared/components/CaseIdBadge";
import { useTranslation } from "../../../i18n";
import { deleteDeletionCase, formatCaseError } from "../casesApi";

const DeleteDeletionCaseModal = ({ open, caseData, onClose, onDeleted }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setError("");
  }, [open, caseData?.id]);

  if (!open || !caseData) return null;

  const reviewCount = caseData.items?.length ?? 0;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError("");
    try {
      await deleteDeletionCase(caseData.id);
      onDeleted?.(caseData);
      onClose?.();
    } catch (err) {
      setError(formatCaseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} labelledBy="delete-case-modal-title">
      <div className="p-6 sm:p-8">
        <div className="mb-5 flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-error-container text-on-error-container">
            <MaterialIcon name="delete_forever" className="text-[24px]" />
          </span>
          <div className="min-w-0 flex-1">
            <h2
              id="delete-case-modal-title"
              className="font-headline-md text-headline-md text-on-surface"
            >
              {t("cases.deleteModal.title")}
            </h2>
            <p className="mt-2 text-body-md text-on-surface-variant">
              {t("cases.deleteModal.subtitle")}
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-3">
          <CaseIdBadge caseData={caseData} variant="enterprise" />
          <p className="mt-2 font-semibold text-on-surface">{caseData.violationType}</p>
          <p className="mt-1 text-label-sm text-on-surface-variant">
            {t("cases.deleteModal.reviewCount", { count: reviewCount })}
          </p>
        </div>

        <p className="mt-4 text-body-md text-on-surface-variant">
          {t("cases.deleteModal.hint")}
        </p>

        {error ? (
          <p className="mt-4 rounded-lg border border-error-container bg-error-container px-4 py-2 text-label-sm text-on-error-container">
            {error}
          </p>
        ) : null}

        {/* Keeping the case is the safe, dominant action; withdrawing is a
            deliberately quiet text link so it can't be clicked by reflex. */}
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="inline-flex h-12 items-center justify-center px-4 text-[15px] font-semibold text-error transition hover:underline disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? t("cases.deleteModal.deleting") : t("cases.deleteModal.confirm")}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-12 items-center justify-center rounded-full bg-primary px-6 text-[15px] font-semibold text-on-primary transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("cases.deleteModal.cancel")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteDeletionCaseModal;
